import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";

export default function Login() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && user) navigate("/calendar", { replace: true });
    }, [user, loading, navigate]);

    const handleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/calendar";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F9F9F8]">
            {/* Left column - form */}
            <div className="flex flex-col justify-between p-10 lg:p-16 border-r border-[#E5E5E3]">
                <div className="flex items-center gap-3" data-testid="login-logo">
                    <img
                        src="https://customer-assets.emergentagent.com/job_group-calendar-7/artifacts/qy8qz9tb_SF%20WF%20logo%20%281%29.png"
                        alt="Scratchfirst"
                        className="h-10 w-auto"
                    />
                    <span className="text-[10px] tracking-[0.2em] text-[#888885] uppercase pl-3 border-l border-[#E5E5E3]">Company calendar</span>
                </div>

                <div className="max-w-md">
                    <p className="text-xs tracking-[0.2em] text-[#888885] uppercase mb-6">01 &mdash; Sign in</p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#111110] leading-[1.05]">
                        A calmer way to <em className="not-italic border-b border-[#111110] pb-1">plan together.</em>
                    </h1>
                    <p className="mt-6 text-[#666664] text-base leading-relaxed">
                        One shared space for every company activity &mdash; lunches, talks, off-sites, run clubs. Post, sign up, and show up.
                    </p>

                    <div className="mt-10 space-y-4">
                        <Button
                            data-testid="google-login-btn"
                            onClick={handleLogin}
                            className="w-full h-14 rounded-none bg-[#111110] hover:bg-[#333333] text-white font-medium text-base tracking-tight justify-between group"
                        >
                            <span className="flex items-center gap-3">
                                <GoogleMark />
                                Continue with Google
                            </span>
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                        </Button>
                        <p className="text-xs text-[#888885] tracking-wide" data-testid="domain-hint">
                            Only <span className="text-[#111110] font-medium">@scratchfirst.co</span> email addresses can sign in.
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-between text-xs text-[#888885] tracking-wide">
                    <span>© {new Date().getFullYear()} Scratchfirst</span>
                    <span className="tracking-[0.2em] uppercase">Internal tool</span>
                </div>
            </div>

            {/* Right column - hero */}
            <div className="hidden lg:block relative overflow-hidden bg-[#EFEDEA]">
                <img
                    src="https://static.prod-images.emergentagent.com/jobs/2100f983-5013-4935-8106-202072bbd009/images/4a88764f6c8db1b3f68690ae8d4754b4625b0bf29d37ee3c967d3db8d0216ec2.png"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#00000020]" />
                <div className="absolute bottom-10 left-10 right-10">
                    <p className="text-xs tracking-[0.2em] uppercase text-white/80 mb-3">A quiet index of everything happening</p>
                    <p className="font-heading text-white text-2xl sm:text-3xl font-light leading-tight max-w-md">
                        Company life, calmly organized — from all-hands to Friday runs.
                    </p>
                </div>
            </div>
        </div>
    );
}

function GoogleMark() {
    return (
        <span className="w-5 h-5 bg-white flex items-center justify-center">
            <svg viewBox="0 0 48 48" className="w-3.5 h-3.5">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
        </span>
    );
}
